import Vue from 'vue'
import Component from 'vue-class-component'
import { Getter, Mutation } from 'vuex-class'
import { Player, Teams, Team, Pawn, Breaths } from '@/services/store/types'
import {
  UpdateTeamEnzymePayload,
  RemovePlayerMoleculePayload,
  AddPlayerMoleculePayload,
  AddPawnPayload,
  RemovePawnPayload,
  UpdatePawnPayload,
  UpdatePawnAmountPayload,
} from '@/services/store/interfaces'
import { StockMolecule, StockEnzyme } from '@/interfaces/stock'
import ModelMolecule from '@/models/molecule'

@Component
export default class MixinGame extends Vue {
  @Getter('gameIsWonByTeam') public gameIsWonByTeam: string | null
  @Getter('players') public players: Player[]
  @Getter('teams') public teams: Teams
  @Getter('turnID') public turnID: number
  @Getter('tableID') public tableID: number
  @Getter('pawns') public pawns: Pawn[]
  @Getter('activeSeatID') public activeSeatID: number
  @Getter('breaths') public breaths: Breaths

  @Getter('boardX') public boardX: number
  @Getter('boardY') public boardY: number
  @Getter('boardZoomFactor') public boardZoomFactor: number
  @Getter('yourTurn') public yourTurn: boolean


  @Mutation('setYourTurn') public setYourTurn: (yourTurn: boolean) => void
  @Mutation('updateTableID') public updateTableID: (id: number) => void
  @Mutation('setGameIsWonByTeam') public storeSetGameIsWonByTeam: (yourTurn: string) => void
  @Mutation('updatePawnAmount') public storeUpdatePawnAmount: (payload: UpdatePawnAmountPayload) => void
  @Mutation('updateTeamEnzyme') public storeUpdateTeamEnzyme: (payload: UpdateTeamEnzymePayload) => void
  @Mutation('updatePlayerInfo') public storeUpdatePlayerInfo: (payload: any) => void
  @Mutation('removePlayerMolecule') public storeRemovePlayerMolecule: (payload: RemovePlayerMoleculePayload) => void
  @Mutation('addPlayerMolecule') public storeAddPlayerMolecule: (payload: AddPlayerMoleculePayload) => void
  @Mutation('addPawn') public storeAddPawn: (payload: AddPawnPayload) => void
  @Mutation('removePawn') public storeRemovePawn: (payload: RemovePawnPayload) => void
  @Mutation('removePawnByID') public storeRemovePawnByID: (payload: string) => void
  @Mutation('updatePawn') public updatePawn: (payload: UpdatePawnPayload) => void
  @Mutation('increaseTurnID') public increaseTurnID: () => void
  @Mutation('breathe') public breathe: () => void
  @Mutation('setBoardX') public setBoardX: (value: number) => void
  @Mutation('setBoardY') public setBoardY: (value: number) => void
  @Mutation('setBoardZoomFactor') public setBoardZoomFactor: (value: number) => void

  public get activePlayer(): Player {
    return this.players.find((player: Player) => player.seatID === this.activeSeatID) as Player
  }

  public get activeTeam(): Team {
    if (this.activePlayer) {
      return this.teams[this.activePlayer.teamID]
    }

    return this.teams.a
  }

  public checkGameWonByTeam() {
    if (this.isGameWonByTeamA()) {
      this.storeSetGameIsWonByTeam('A')
    }

    if (this.isGameWonByTeamB()) {
      this.storeSetGameIsWonByTeam('B')
    }
  }

  protected getPawn(molecule: ModelMolecule) {
    let index = this.pawns.findIndex((pawn: Pawn) => {
      return pawn.playerID === this.activePlayer.id &&
        pawn.moleculeID === molecule.id
    })

    if (index === -1) {
      index = this.pawns.findIndex((pawn: Pawn) => {
        return pawn.playerID === null &&
          pawn.moleculeID === molecule.id
      })
    }

    if (index === -1) {
      const teamPlayer = this.players.find((item: Player) => {
        return item.teamID === this.activePlayer.teamID && item.id !== this.activePlayer.id
      }) as Player

      index = this.pawns.findIndex((pawn: Pawn) => {
        return pawn.playerID === teamPlayer.id &&
          pawn.moleculeID === molecule.id
      })
    }

    return this.pawns[index]
  }

  private isGameWonByTeamA(): boolean {
    let assignmentCompletePlayer1 = false
    let assignmentCompletePlayer2 = false

    const moleculesPlayer1: StockMolecule[] = this.players[0].molecules
    const moleculesPlayer2: StockMolecule[] = this.players[1].molecules
    const teamEnzymes: StockEnzyme[] = this.teams.a.enzymes

    const tag: StockMolecule | undefined = moleculesPlayer1.find((molecule: StockMolecule) => {
      return molecule.ids.includes('j2971q94')
    })

    const lactaat: StockMolecule | undefined = moleculesPlayer2.find((molecule: StockMolecule) => {
      return molecule.ids.includes('pqxroxg1')
    })

    const co2: StockEnzyme | undefined = teamEnzymes.find((enzyme: StockEnzyme) => {
      return enzyme.id === '0634kzog'
    })

    if (tag &&
      tag.chainLengths.join('-') === '16-18-18' &&
      tag.doubleBindings.join('-') === '0-1-0') {
      assignmentCompletePlayer1 = true
    }

    if (lactaat &&
      co2 &&
      co2.quantity >= 7) {
      assignmentCompletePlayer2 = true
    }

    return assignmentCompletePlayer1 && assignmentCompletePlayer2
  }

  private isGameWonByTeamB(): boolean {
    let assignmentCompletePlayer1 = false
    let assignmentCompletePlayer2 = false

    const moleculesPlayer1: StockMolecule[] = this.players[2].molecules
    const moleculesPlayer2: StockMolecule[] = this.players[3].molecules
    const teamEnzymes: StockEnzyme[] = this.teams.b.enzymes

    const glucose: StockMolecule | undefined = moleculesPlayer1.find((molecule: StockMolecule) => {
      return molecule.ids.includes('0p9d63vw')
    })

    const glycogeen: StockMolecule | undefined = moleculesPlayer1.find((molecule: StockMolecule) => {
      return molecule.ids.includes('m430wxry')
    })

    const lauraat: StockMolecule | undefined = moleculesPlayer2.find((molecule: StockMolecule) => {
      return molecule.ids.includes('1oxv7zxd') &&
        molecule.chainLengths[0] === 12 &&
        molecule.doubleBindings[0] === 0
    })

    const co2: StockEnzyme | undefined = teamEnzymes.find((enzyme: StockEnzyme) => {
      return enzyme.id === '0634kzog'
    })

    if (glucose && glycogeen) {
      assignmentCompletePlayer1 = true
    }

    if (lauraat &&
      co2 &&
      co2.quantity >= 7) {
      assignmentCompletePlayer2 = true
    }

    return assignmentCompletePlayer1 && assignmentCompletePlayer2
  }
}
